"use client";
import { useEffect, useMemo, useState } from "react";
import { getSettings, CreditSettings } from "@/lib/credits";

type Theme = "system" | "light" | "dark";

function applyTheme(theme: Theme) {
  try {
    const systemDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const isDark = theme === "dark" || (theme === "system" && systemDark);
    const root = document.documentElement;
    root.classList.toggle("dark", isDark);
    root.classList.toggle("light", !isDark);
  } catch {}
}

export default function SettingsPanel() {
  const [theme, setTheme] = useState<Theme>("system");
  const [settings, setSettings] = useState<CreditSettings | null>(null);

  // Read local usage stored by app
  const [usageMap, setUsageMap] = useState<Record<string, number>>({});

  useEffect(() => {
    try {
      const saved = (localStorage.getItem("theme") as Theme) || "system";
      setTheme(saved);
      applyTheme(saved);
    } catch {}
  }, []);
  useEffect(() => { (async()=>{ try { setSettings(await getSettings()); } catch {} })(); }, []);
  useEffect(() => {
    const calc = () => {
      const per: Record<string, number> = {};
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = String(localStorage.key(i));
          if (!k.startsWith('ai_usage_totals:')) continue;
          const conv = k.split(':')[1];
          const totals = JSON.parse(localStorage.getItem(k) || 'null');
          const last = JSON.parse(localStorage.getItem(`ai_usage_last:${conv}`) || 'null');
          const model = last?.model ? String(last.model) : null;
          if (!model || !totals) continue;
          per[model] = (per[model] || 0) + Number(totals.total || 0);
        }
      } catch {}
      setUsageMap(per);
    };
    calc();
    const onUsage = () => calc();
    const onStorage = () => calc();
    window.addEventListener('ai-usage' as any, onUsage);
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('ai-usage' as any, onUsage); window.removeEventListener('storage', onStorage); };
  }, []);

  function onChoose(next: Theme) {
    setTheme(next);
    try { localStorage.setItem("theme", next); } catch {}
    applyTheme(next);
  }

  const Item = ({ id, title, desc }: { id: Theme; title: string; desc: string }) => (
    <button
      onClick={() => onChoose(id)}
      className={`w-full text-left rounded-md border px-4 py-3 transition-colors ${
        theme === id
          ? "border-indigo-500 bg-gray-800/70 text-gray-100"
          : "border-gray-600 bg-gray-800/40 text-gray-200 hover:bg-gray-800/60"
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-[12px] text-gray-400">{desc}</div>
        </div>
        {theme === id && (
          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-600 text-white text-xs">�  </span>
        )}
      </div>
    </button>
  );

  return (
    <div className="flex h-full w-full flex-col bg-gray-900">
      {/* Header */}
      <div className="h-12 flex items-center justify-between px-4 border-b border-gray-700 text-gray-200 text-sm font-medium">
        <span>Settings</span>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* My Usage based on model limits */}
        <div className="rounded-md border border-gray-700 bg-gray-800/60 p-4">
          <div className="text-gray-100 text-sm font-semibold mb-3">My Usage</div>
          {/* Model Limits progress */}
          <ModelLimitsCompact settings={settings} usageMap={usageMap} />
        </div>
        <div className="rounded-md border border-gray-700 bg-gray-800/60 p-4">
          <div className="text-gray-100 text-sm font-semibold mb-3">Theme</div>
          <div className="space-y-2">
            <Item id="system" title="System" desc="Use device appearance" />
            <Item id="light" title="Light" desc="Bright theme" />
            <Item id="dark" title="Dark" desc="Dim theme for low light" />
          </div>
        </div>

        <div className="rounded-md border border-gray-700 bg-gray-800/60 p-4">
          <div className="text-gray-100 text-sm font-semibold mb-2">About theme</div>
          <div className="text-[13px] text-gray-400">
            Changes apply instantly and persist in this browser (localStorage: "theme").
          </div>
        </div>
      </div>
    </div>
  );
}

function ModelLimitsCompact({ settings, usageMap }: { settings: CreditSettings | null; usageMap: Record<string, number> }) {
  const hasLimits = settings?.modelLimits && Object.keys(settings.modelLimits).length > 0;

  // Load settings and usage once when component mounts via parent
  useEffect(() => {
    (async () => {
      try { const s = await getSettings(); (window as any).__tmpSetSettings?.(s); } catch {}
    })();
  }, []);

  // Hook from parent: SettingsPanel initializes and stores values here
  useEffect(() => {
    // Wire a simple bridge to let parent update state
    (window as any).__tmpSetSettings = (s: CreditSettings) => {};
    return () => { delete (window as any).__tmpSetSettings; };
  }, []);

  // If parent hasn't provided settings/usage, compute locally
  const [localSettings, setLocalSettings] = useState<CreditSettings | null>(null);
  const [localUsage, setLocalUsage] = useState<Record<string, number>>({});

  useEffect(() => { (async()=>{ if (!settings) { try { setLocalSettings(await getSettings()); } catch {} } })(); }, [settings]);
  useEffect(() => {
    if (Object.keys(usageMap||{}).length) return;
    const perModel: Record<string, number> = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = String(localStorage.key(i));
        if (!k.startsWith('ai_usage_totals:')) continue;
        const conv = k.split(':')[1];
        const totals = JSON.parse(localStorage.getItem(k) || 'null');
        const last = JSON.parse(localStorage.getItem(`ai_usage_last:${conv}`) || 'null');
        const model = last?.model ? String(last.model) : null;
        if (!model || !totals) continue;
        perModel[model] = (perModel[model] || 0) + Number(totals.total || 0);
      }
    } catch {}
    setLocalUsage(perModel);
  }, [usageMap]);

  const lims = (settings || localSettings)?.modelLimits || {};
  const useMap = Object.keys(usageMap||{}).length ? usageMap : localUsage;
  const entries = Object.entries(lims);

  if (!entries.length) {
    return <div className="text-[13px] text-gray-400">ยังไม่มีการตั้งค่า limit ต่อโมเดล</div>;
  }

  return (
    <div className="space-y-3">
      {entries.map(([model, limit]) => {
        const used = useMap[model] || 0;
        const pct = limit ? Math.min(100, Math.round((used/Number(limit))*100)) : 0;
        return (
          <div key={model} className="space-y-1">
            <div className="flex justify-between text-sm text-gray-300">
              <span className="truncate" title={model}>{model}</span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 rounded bg-slate-700 overflow-hidden">
              <div className={`h-1.5 ${pct>=100? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="text-[11px] text-gray-400">{used} / {limit} tokens</div>
          </div>
        );
      })}
    </div>
  );
}
